// SASTRun1Excel.js - Updated to use only real SAST response data
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

export async function exportToSastXLSRun1(
  scanResults,
  serviceName,
  runType = "Run1"
) {
  try {
    // Transform SAST data using only actual response data
    const transformedData = transformSastDataForExcel(scanResults);

    // Create worksheet
    const worksheet = XLSX.utils.json_to_sheet(transformedData);

    // Set column widths
    const colWidths = [
      { wch: 35 }, // Vulnerability Name
      { wch: 80 }, // Description
      { wch: 100 }, // Filepath
      { wch: 12 }, // Line Number
      { wch: 12 }, // Severity
      { wch: 60 }, // Recommendations
      { wch: 20 }, // Vulnerability Identified Date
      { wch: 20 }, // Vulnerability Status
      { wch: 20 }, // Total Lines of Code (LoC)
    ];
    worksheet["!cols"] = colWidths;

    // Add data validation for Vulnerability Status dropdown
    addDropdownValidation(worksheet, transformedData.length);

    // Apply conditional formatting for severity
    applySeverityFormatting(worksheet, transformedData.length);

    // Create workbook
    const workbook = XLSX.utils.book_new();
    const sheetName = `SAST Scan Report ${runType}`;
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    // Generate file with File Explorer
    const timestamp = getDetailedTimestamp();
    const filename = `SAST-Scan-Clean-Report-${runType}_${timestamp}.xlsx`;

    const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });

    await saveExcelWithFilePicker(blob, filename, runType);
  } catch (error) {
    console.error("Error generating SAST Excel report:", error);
    alert("Failed to generate Excel report. Please try again.");
  }
}

function getDetailedTimestamp() {
  const now = new Date();
  const pad = (num) => num.toString().padStart(2, "0");

  const year = now.getFullYear();
  const month = pad(now.getMonth() + 1);
  const day = pad(now.getDate());
  const hours = pad(now.getHours());
  const minutes = pad(now.getMinutes());
  const seconds = pad(now.getSeconds());

  return `${year}-${month}-${day}_${hours}-${minutes}-${seconds}`;
}

function transformSastDataForExcel(scanResults) {
  const transformedRows = [];

  scanResults.forEach((item) => {
    const lines = item.lines || item.Lines || [item.Line || item.original_line];
    const filepath = extractRelevantPath(item.File || item.file || "");

    if (Array.isArray(lines) && lines.length > 0) {
      // Create a row for each line number
      lines.forEach((lineNumber, index) => {
        const row = createExcelRowWithRealData(
          item,
          filepath,
          lineNumber,
          index === 0
        );
        transformedRows.push(row);
      });
    } else {
      // Single row if no lines array
      const row = createExcelRowWithRealData(
        item,
        filepath,
        lines[0] || "",
        true
      );
      transformedRows.push(row);
    }
  });

  return transformedRows;
}

// 🔥 UPDATED: Only use real data from SAST response
function createExcelRowWithRealData(
  item,
  filepath,
  lineNumber,
  includeDetails
) {
  return {
    "Vulnerability Name": includeDetails ? item.vulnerability_name || "" : "",
    Description: includeDetails ? item.description || "" : "",
    Filepath: filepath,
    "Line Number": lineNumber || "",
    Severity: includeDetails
      ? normalizeSeverity(item.Impact || item.impact)
      : "",
    Recomendations: includeDetails
      ? formatRecommendations(item.recommendation)
      : "",
    // 🔥 ONLY use actual date from response, no defaults
    "Vulnerability Identified Date": includeDetails
      ? item.vulnerability_identified_date ||
        item.Vulnerability_Identified_Date ||
        ""
      : "",
    // 🔥 ONLY use actual status from response, no defaults
    "Vulnerability Status": includeDetails
      ? item.vulnerability_status || item.Vulnerability_Status || ""
      : "",
    // 🔥 ONLY use actual LoC from response, no defaults
    "Total Lines of Code (LoC)": includeDetails
      ? item.total_lines_of_code || item.Total_Lines_of_Code || ""
      : "",
  };
}

function extractRelevantPath(fullPath) {
  if (!fullPath) return "";
  const normalizedPath = fullPath.replace(/\\/g, "/");
  const match = normalizedPath.match(/(?:giturl|[^/]+_\d+)\/(.+)/);
  return match ? match[1] : fullPath;
}

function normalizeSeverity(severity) {
  if (!severity) return "";
  const sev = severity.toString().toUpperCase();
  switch (sev) {
    case "CRITICAL":
    case "BLOCKER":
      return "Critical";
    case "HIGH":
      return "High";
    case "MEDIUM":
    case "MAJOR":
      return "Medium";
    case "LOW":
    case "MINOR":
      return "Low";
    default:
      return severity;
  }
}

// 🔥 UPDATED: Only format if recommendations exist
function formatRecommendations(recommendations) {
  if (!recommendations || typeof recommendations !== "object") return "";

  return Object.entries(recommendations)
    .map(([language, solution]) => `${language}:\n${solution}`)
    .join("\n\n");
}

function addDropdownValidation(worksheet, dataLength) {
  const statusOptions = ["TP", "FP"];

  // Add data validation only for cells that will have status values
  if (!worksheet["!dataValidation"]) {
    worksheet["!dataValidation"] = [];
  }

  worksheet["!dataValidation"].push({
    type: "list",
    allowBlank: true, // 🔥 Allow blank for empty cells
    showInputMessage: true,
    showErrorMessage: true,
    formula1: '"TP,FP"',
    sqref: `H2:H${dataLength + 1}`,
  });
}

function applySeverityFormatting(worksheet, dataLength) {
  // Apply conditional formatting only to cells with severity data
  for (let row = 2; row <= dataLength + 1; row++) {
    const severityCell = `E${row}`;
    if (worksheet[severityCell] && worksheet[severityCell].v) {
      const severity = worksheet[severityCell].v;
      let bgColor;

      switch (severity) {
        case "Critical":
          bgColor = "FFAE241E";
          break;
        case "High":
          bgColor = "FFFF4040";
          break;
        case "Medium":
          bgColor = "FFFFA500";
          break;
        case "Low":
          bgColor = "FF388E3C";
          break;
        default:
          continue; // Skip formatting for empty/unknown values
      }

      if (!worksheet[severityCell].s) {
        worksheet[severityCell].s = {};
      }
      worksheet[severityCell].s.fill = {
        fgColor: { rgb: bgColor },
      };
    }
  }
}

// File picker function remains the same
async function saveExcelWithFilePicker(blob, suggestedName, runType) {
  if (window.showSaveFilePicker) {
    try {
      const fileHandle = await window.showSaveFilePicker({
        suggestedName: suggestedName,
        types: [
          {
            description: "Excel files",
            accept: {
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
                [".xlsx"],
            },
          },
        ],
        excludeAcceptAllOption: true,
      });

      const writable = await fileHandle.createWritable();
      await writable.write(blob);
      await writable.close();

      // console.log(
      //   `SAST ${runType} Excel report saved successfully to:`,
      //   fileHandle.name
      // );

      if (typeof window !== "undefined" && window.alert) {
        setTimeout(() => {
          alert(`SAST ${runType} report saved successfully!`);
        }, 100);
      }
    } catch (err) {
      if (err.name === "AbortError") {
        // console.log("User cancelled file save");
        return;
      } else {
        console.error("File save error:", err);
        saveAs(blob, suggestedName);
      }
    }
  } else {
    // console.log("File System Access API not supported, using automatic download"
    // );
    saveAs(blob, suggestedName);
  }
}
